import { 
  BarChart3, 
  ListChecks, 
  TrendingUp, 
  CheckSquare, 
  FolderKanban, 
  FileText, 
  DollarSign, 
  Target, 
  Zap,
  Settings as SettingsIcon
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ModulesShowcase = () => {
  const modules = [
    {
      id: "dashboard",
      icon: BarChart3,
      title: "Dashboard",
      description: "See total sales, pipeline value, income, expenses, net profit, task completion rate, and client risk at a glance. A daily AI banner explains how your month is going and whether you're on track."
    },
    {
      id: "super-queue",
      icon: ListChecks,
      title: "Super Queue (Unified Inbox)",
      description: "Your day, ranked by impact. The Super Queue pulls in high-value deals, overdue invoices, critical tasks, at-risk clients, and lagging goals — then lets you pick your top 6 priorities for today."
    },
    {
      id: "sales",
      icon: TrendingUp,
      title: "Sales (CRM)",
      description: "Simple Kanban and list views with stages from Lead → Discovery → Proposal → Negotiation → Won/Lost. Track deal value, close probability, and reach out via one-click WhatsApp, call, or email."
    },
    {
      id: "taskflow",
      icon: CheckSquare,
      title: "TaskFlow",
      description: "Tasks that always know which client and project they belong to. Use an inbox and board view, set priorities, configure recurring tasks, and use quick actions like 'Do today', 'Snooze', and 'Complete'."
    },
    {
      id: "projects-clients",
      icon: FolderKanban,
      title: "Projects & Clients",
      description: "Clients show total revenue and relationship health. Projects sit under each client with deadlines and progress. Tasks, invoices, and goals all link back to projects and clients, so context is never lost."
    },
    {
      id: "invoices-finance",
      icon: FileText,
      title: "Invoices & Finance",
      description: "Create quotes, one-time invoices, and recurring invoices, all linked to clients. Finance shows income, expenses, net profit, outstanding amounts, and clear charts like income vs expense and top clients by revenue."
    },
    {
      id: "finance",
      icon: DollarSign,
      title: "Finance Tracking",
      description: "Track all your income and expenses in one place. See your net profit, cash flow trends, and financial health with beautiful charts and insights. Every transaction links back to clients and projects."
    },
    {
      id: "goals",
      icon: Target,
      title: "Goals",
      description: "Set revenue, sales, task, project, or expense goals. Connect them to real data from invoices, deals, and tasks. See progress bars, 'on track / needs push / at risk' labels, and AI forecasts based on your pace."
    },
    {
      id: "quick-capture",
      icon: Zap,
      title: "Quick Capture",
      description: "Log anything in one line from anywhere in the app. Turn natural language into structured leads, tasks, invoices, income, or expenses — with optional voice capture when you're on the move."
    },
    {
      id: "settings",
      icon: SettingsIcon,
      title: "Settings",
      description: "Configure your business details, logo, tax info, timezone, currency, and number format. Switch themes, adjust security, and export your data so Catlyo feels like your freelance headquarters."
    }
  ];

  return (
    <section className="py-20 bg-muted/50">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Every part of your freelance business, stitched together.
          </h2>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <AccordionItem 
                key={module.id} 
                value={module.id}
                className="border border-border/50 rounded-lg bg-card px-6 data-[state=open]:border-primary/50"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-4 text-left">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-xl font-semibold">{module.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <p className="text-base text-muted-foreground leading-relaxed pl-14">
                    {module.description}
                  </p>
                  {/* Screenshot placeholder */}
                  <div className="mt-6 ml-14 aspect-video bg-muted rounded-lg flex items-center justify-center border border-border/50">
                    <span className="text-sm text-muted-foreground">
                      {module.title} Screenshot (Coming Soon)
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
};

export default ModulesShowcase;
